import { type ReactElement, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { Banner, Button, ButtonGroup, TextLink } from 'slab'
import {
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentsEnroll as useCreateEnrollment,
  usePostMemberOnboardingEnrollmentEventsEnrollmentEventIdSelectedPlansCreateMedicareSelectedPlanPlaceholder as useCreateMedicarePlaceholder
} from 'utils/web_api'

import { CircleItemBulletPoint } from '../../reusable/CircleItemBulletPoint'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { OnboardingGenericErrorMessage } from './OnboardingGenericErrorMessage'
import { ALERA_MEDICARE_FORM_URL, isRenewalOrQleFlow, MEDICARE_FORM_URL } from '../utils'

export function MedicareDisplay ({
  navigateToNextStep,
  navigateToPreviousStep,
}: StepComponentProps) {
  const { enrollmentEventId, onboardingData, refreshOnboardingInfo, useAleraMedicareForm, newQleEnabled } = useOnboarding()
  const createMedicarePlaceholder = useCreateMedicarePlaceholder()
  const createEnrollment = useCreateEnrollment()
  const [submitError, setSubmitError] = useState<ReactElement | null>(null)

  const medicareFormUrl = useAleraMedicareForm
    ? ALERA_MEDICARE_FORM_URL
    : MEDICARE_FORM_URL

  const handleCreateMedicarePlaceholder = () => {
    if (!onboardingData) {
      return
    }

    // For renewal, create placeholder SelectedPlan
    if (isRenewalOrQleFlow(onboardingData, newQleEnabled)) {
      createMedicarePlaceholder.mutate(
        { enrollmentEventId },
        {
          onSuccess: async () => {
            const refreshedData = await refreshOnboardingInfo()
            navigateToNextStep?.(refreshedData!)
          },
          onError: (error) => {
            const errorMessage = (
              <OnboardingGenericErrorMessage
                error={error as GrapeAPIErrorType}
              />
            )
            setSubmitError(errorMessage)
          },
        }
      )
    } else { // For non-renewal, create placeholder Enrollment
      createEnrollment.mutate(
        {
          enrollmentEventId,
          data: {
            covers_medical: true,
            covers_dental: false,
            covers_vision: false,
            plan_code: 'Medicare',
            carrier_name: 'Medicare',
            quoted_premium: 0,
          },
        },
        {
          onSuccess: async () => {
            const refreshedData = await refreshOnboardingInfo()
            navigateToNextStep?.(refreshedData!)
          },
          onError: (error) => {
            const errorMessage = (
              <OnboardingGenericErrorMessage
                error={error as GrapeAPIErrorType}
              />
            )
            setSubmitError(errorMessage)
          },
        }
      )
    }
  }
  return (
    <div className='mb-10 max-w-[44rem]'>
      <h1 className='heading-lg text-gray-800 mb-3'>
        Use your Thatch budget on Medicare
      </h1>
      <p className='text-gray-500'>
        Someone in your family is 65 or older, or will be in the next few
        months, which means it's time to move to Medicare.
      </p>
      <div className='p-6 my-4 border border-gray-200 rounded-lg'>
        <p className='text-md font-semibold'>
          To get access to your Thatch budget, you'll need to:
        </p>
        <ul className='list-none body pl-4 pt-4 mb-4'>
          <li className='mb-2'>
            <div className='flex items-start space-x-2'>
              <CircleItemBulletPoint label='1' />
              <div className='flex flex-col gap-y-2'>
                <span className='flex-shrink-1'>
                  <TextLink
                    target='_blank'
                    color='purple'
                    href={medicareFormUrl}
                  >
                    Meet with a licensed advisor
                  </TextLink>{' '}
                  to help you find the right Medicare coverage for your needs
                </span>
                <span className='body-sm mt-1 text-gray-500'>
                  Anyone who's under 65 will need to get an{' '}
                  <TextLink
                    target='_blank'
                    href='https://support.thatch.com/eligible-medical-plans#eligible-plans-for-individuals-under-65-years-of-age'
                  >
                    individual or family plan
                  </TextLink>{' '}
                  (IFP) for their insurance. Our licensed advisors can help with
                  those, too!
                </span>
                <span className='body-sm mt-1 text-gray-500'>
                  If you and your dependents already have plans that you're
                  happy with,{' '}
                  <TextLink
                    target='_blank'
                    href={medicareFormUrl}
                  >
                    send us your plan information
                  </TextLink>{' '}
                  so you can get reimbursed for those premiums.
                </span>
              </div>
            </div>
          </li>
          <li className='mb-3'>
            <div className='flex items-center space-x-2'>
              <CircleItemBulletPoint label='2' />
              <span className='flex-shrink-1 mt-1'>
                Return to this page to finish signing up on Thatch
              </span>
            </div>
          </li>
        </ul>
      </div>
      {submitError && (
        <div className='mt-4'>
          <Banner
            title='A problem has occurred while processing your request'
            type='critical'
          >
            {submitError}
          </Banner>
        </div>
      )}
      <div className='mt-10'>
        <ButtonGroup size='large'>
          <Button
            variant='secondary'
            disabled={createMedicarePlaceholder.isPending || createEnrollment.isPending}
            onClick={() => navigateToPreviousStep?.(onboardingData)}
          >
            Back
          </Button>
          <Button
            variant='primary'
            disabled={createMedicarePlaceholder.isPending || createEnrollment.isPending}
            onClick={handleCreateMedicarePlaceholder}
          >
            Continue
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}
